"use client";

import * as React from "react";
import {
  Home,
  CreditCard,
  Megaphone,
  Bus,
  FileCheck,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { API_ENDPOINTS } from "../constants/config";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { clearStoredAuth, getToken } from "../utils/authStorage";
import axios from "axios";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { filterMenuByFeatures } from "../utils/featureMenuFilter";

export function StudentSidebar({ schoolSettings, children }) {
  const [user, setUser] = React.useState(null);
  const [resolvedSchoolSettings, setResolvedSchoolSettings] = React.useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split("/");
  const currentTab = currentPath[2] || "fees";
  const { features } = useFeatureFlag();

  React.useEffect(() => {
    // Fetch user profile from API
    const fetchUserProfile = async () => {
      try {
        setLoading(true);

        // Get the authentication token from localStorage
        const token = getToken();

        if (!token) {
          // Redirect to login page if token is not found
          window.location.href = "/login";
          return;
        }

        const response = await fetch(API_ENDPOINTS.usersProfile, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          clearStoredAuth();
          window.location.href = "/login";
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === "success" && data.data && data.data.user) {
          setUser(data.data.user);

          // Update localStorage with fresh user data
          localStorage.setItem("user", JSON.stringify(data.data.user));
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError(err.message);

        // Fallback to localStorage if API call fails
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.error("Error parsing stored user data:", e);
            // If stored user data is invalid, redirect to login
            clearStoredAuth();
            window.location.href = "/login";
          }
        } else {
          // No stored user data, redirect to login
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  React.useEffect(() => {
    const fetchSchoolSettingsFallback = async () => {
      if (schoolSettings) {
        setResolvedSchoolSettings(schoolSettings);
        return;
      }
      try {
        const token = getToken();
        if (!token) return;

        const profileRes = await axios.get(API_ENDPOINTS.usersProfile, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const profileUser = profileRes.data?.data?.user;
        const schoolId =
          profileUser?.school &&
          (typeof profileUser.school === "object" ? profileUser.school._id : profileUser.school);
        if (!schoolId) return;

        const settingsRes = await axios.get(API_ENDPOINTS.schoolFeatures(schoolId), {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        setResolvedSchoolSettings(settingsRes.data?.data || null);
      } catch (fetchErr) {
        console.error("Failed to fetch school settings:", fetchErr);
      }
    };

    fetchSchoolSettingsFallback();
  }, [schoolSettings]);

  const menuItems = [
    { id: "fees", label: "Fees", icon: <CreditCard className="w-5 h-5" /> },
    {
      id: "announcements",
      label: "Announcements",
      icon: <Megaphone className="w-5 h-5" />,
    },
    {
      id: "leave",
      label: "Leave Application",
      icon: <FileCheck className="w-5 h-5" />,
    },
    {
      id: "bustracking",
      label: "Bus Tracking",
      icon: <Bus className="w-5 h-5" />,
    },
  ];

  // Apply feature flag filtering
  const filteredMenuItems = filterMenuByFeatures(menuItems, features, 'student');

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Helper function to get full name from user data
  const getFullName = (user) => {
    if (!user) return "Student";

    const firstName = user.firstName || "";
    const middleName = user.middleName || "";
    const lastName = user.lastName || "";

    return (
      [firstName, middleName, lastName].filter(Boolean).join(" ") || "Student"
    );
  };

  // Helper function to get initials for avatar fallback
  const getInitials = (user) => {
    if (!user || !user.firstName) return "S";

    const firstInitial = user.firstName.charAt(0);
    const lastInitial = user.lastName ? user.lastName.charAt(0) : "";

    return (firstInitial + lastInitial).toUpperCase();
  };

  // Helper function to get student ID display
  const getStudentId = (user) => {
    if (!user) return "Loading...";
    return user.admissionNumber || "No ID";
  };

  // Render loading state
  if (loading && !user) {
    return (
      <div className="flex h-full bg-gray-50">
        <div className="flex items-center justify-center w-full">
          <div className="text-sm text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 relative overflow-hidden">
      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        className="lg:hidden fixed top-4 left-4 z-40 p-2"
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Menu className="w-6 h-6" />
        )}
      </Button>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Responsive Sidebar */}
      <aside
        className={`
          fixed lg:static
          top-20 lg:top-0 left-0 bottom-0
          w-[280px] lg:w-72
          bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
          z-40
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Brand Section */}
          <div className="flex-shrink-0 p-6 border-b border-gray-200">
            <button
              onClick={() => {
                navigate("/dashboard/profile");
                if (window.innerWidth < 1024) {
                  setIsSidebarOpen(false);
                }
              }}
              className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity cursor-pointer"
            >
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={user?.profilePicture || ""}
                  alt={getFullName(user)}
                />
                <AvatarFallback>{getInitials(user)}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <h1 className="text-lg font-bold text-gray-800">
                  {getFullName(user)}
                </h1>
                <p className="text-sm text-gray-500">{getStudentId(user)}</p>
              </div>
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 p-4 overflow-y-auto overflow-x-hidden min-h-0">
            <div className="space-y-1">
              {filteredMenuItems.map((item) => (
                <Button
                  key={item.id}
                  variant={currentTab === item.id ? "secondary" : "ghost"}
                  className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                    currentTab === item.id
                      ? "bg-[#0263cc] text-white"
                      : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                  }`}
                  onClick={() => {
                    const path = `/dashboard/${item.id}`;
                    navigate(path);
                    if (window.innerWidth < 1024) {
                      setIsSidebarOpen(false);
                    }
                  }}
                >
                  {item.icon}
                  <span className="ml-3 whitespace-nowrap">{item.label}</span>
                </Button>
              ))}
            </div>
          </nav>

          {/* Logout Button - Fixed at bottom */}
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px]"
              onClick={() => {
                clearStoredAuth();
                window.location.href = "/login";
              }}
            >
              <LogOut className="w-5 h-5" />
              <span className="ml-3">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto lg:ml-0 w-full">
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
